/**
 * Branded identifiers.
 *
 * Every ID in this system is a string, which means the compiler will happily let
 * a lesson ID be passed where an exercise ID belongs. Branding closes that hole:
 * the brand exists only in the type system and costs nothing at runtime, but
 * `LessonId` and `ExerciseId` stop being interchangeable.
 *
 * Two shapes exist, because the data model has two kinds of identity
 * (docs/DATA-MODEL.md):
 *
 * - **Content IDs** are readable, hierarchical slugs authored by hand —
 *   `en.b1.c3.l2`. They are stable forever: progress keys off them, so renaming
 *   one orphans a child's history (D-046, and the content linter enforces it).
 * - **Record IDs** are UUIDs for rows about a learner. Attempts generate theirs
 *   on the client so a retried request cannot double-record an answer (D-050).
 */
/**
 * Thrown when a string cannot be a given kind of ID. Carries the offending value
 * so a content lint failure names the thing that is wrong, not just the rule.
 */
export class InvalidIdError extends Error {
    kind;
    value;
    constructor(kind, value, reason) {
        super(`invalid ${kind} ${JSON.stringify(value)}: ${reason}`);
        this.kind = kind;
        this.value = value;
        this.name = 'InvalidIdError';
    }
}
/**
 * Dotted lowercase segments: `en`, `en.b1`, `en.b1.c3.l2`.
 *
 * Deliberately strict. These IDs end up in URLs, filenames and MySQL keys, and a
 * stray space or capital would work in three of those and fail in the fourth.
 */
const CONTENT_ID = /^[a-z0-9]+(?:[_-][a-z0-9]+)*(?:\.[a-z0-9]+(?:[_-][a-z0-9]+)*)*$/;
/** A single lowercase segment: `party-hat`, `grape`, `sprout`. */
const SLUG_ID = /^[a-z0-9]+(?:[_-][a-z0-9]+)*$/;
/** Canonical lowercase UUID, any version. */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
/** How many dotted segments an ID of each depth must have. */
const CONTENT_DEPTH = {
    CourseId: 1,
    BookId: 2,
    ChapterId: 3,
    LessonId: 4,
};
function assertContentId(kind, value) {
    if (!CONTENT_ID.test(value)) {
        throw new InvalidIdError(kind, value, 'expected dot-separated lowercase segments, e.g. "en.b1.c3.l2"');
    }
    const depth = value.split('.').length;
    const expected = CONTENT_DEPTH[kind];
    if (depth !== expected) {
        throw new InvalidIdError(kind, value, `expected ${String(expected)} dotted segment(s), got ${String(depth)}`);
    }
}
export const courseId = (v) => {
    assertContentId('CourseId', v);
    return v;
};
export const bookId = (v) => {
    assertContentId('BookId', v);
    return v;
};
export const chapterId = (v) => {
    assertContentId('ChapterId', v);
    return v;
};
export const lessonId = (v) => {
    assertContentId('LessonId', v);
    return v;
};
/**
 * Exercises, lexemes, phrases and skills sit under a course but are not part of
 * the book/chapter/lesson ladder, so they are checked for shape rather than
 * depth: `en.sk.question_forms`, `en.lex.apple`.
 */
function contentRef(kind) {
    return (v) => {
        if (!CONTENT_ID.test(v)) {
            throw new InvalidIdError(kind, v, 'expected dot-separated lowercase segments, e.g. "en.sk.question_forms"');
        }
        return v;
    };
}
export const exerciseId = contentRef('ExerciseId');
export const lexemeId = contentRef('LexemeId');
export const phraseId = contentRef('PhraseId');
export const skillId = contentRef('SkillId');
function uuidRef(kind) {
    return (v) => {
        if (!UUID.test(v)) {
            throw new InvalidIdError(kind, v, 'expected a lowercase UUID');
        }
        return v;
    };
}
export const accountId = uuidRef('AccountId');
export const learnerId = uuidRef('LearnerId');
export const deviceId = uuidRef('DeviceId');
export const sessionId = uuidRef('SessionId');
export const attemptId = uuidRef('AttemptId');
export const chestId = uuidRef('ChestId');
function slugRef(kind) {
    return (v) => {
        if (!SLUG_ID.test(v)) {
            throw new InvalidIdError(kind, v, 'expected one lowercase slug, e.g. "party-hat"');
        }
        return v;
    };
}
export const itemId = slugRef('ItemId');
export const dyeId = slugRef('DyeId');
export const petId = slugRef('PetId');
export const foodId = slugRef('FoodId');
export const titleId = slugRef('TitleId');
export const achievementId = slugRef('AchievementId');
/** The chapter a lesson belongs to, from the lesson's own ID. */
export const chapterOf = (lesson) => lesson.split('.').slice(0, 3).join('.');
/** The book a chapter belongs to. */
export const bookOf = (chapter) => chapter.split('.').slice(0, 2).join('.');
/** The course anything content-shaped belongs to. */
export const courseOf = (id) => id.split('.')[0];
//# sourceMappingURL=ids.js.map